/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file initializeTrackerLifecycle internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\initialize-tracker-lifecycle
 */

import { Dispatch } from "react";
import { IAction, IAsyncFunction, TParameters } from "../";
import { buildInstanceFunctions } from "./build-instance-functions";
import { handleTrackerUnmount } from "./handle-tracker-unmount";
import {
	AsyncFinishersRef,
	ExecutorRef,
	CallbackExecutorRef,
	IgnorerRef,
	NextIdRef,
	StateUpdaterRef,
} from "../types";
import { IErrorHandler, ISuccessHandler } from "../../types";

/**
 * Set up the mount and unmount lifecycle for the tracker. This function will be executed
 * inside the useEffect hook to attach things to the right lifecycles.
 */
export function initializeTrackerLifecycle<TAsync extends IAsyncFunction>(
	asyncOperation: TAsync,
	executeImmediately: boolean,
	dispatch: Dispatch<IAction>,
	asyncFinishers: AsyncFinishersRef,
	execute: ExecutorRef<TAsync>,
	executeCallbacks: CallbackExecutorRef,
	ignore: IgnorerRef,
	nextId: NextIdRef,
	updateState: StateUpdaterRef,
	initialArguments?: TParameters<TAsync>,
	onFailure?: IErrorHandler<TAsync>,
	onSuccess?: ISuccessHandler<TAsync>,
): () => void {
	// Build all the workhorse functions
	buildInstanceFunctions(
		asyncOperation,
		dispatch,
		asyncFinishers,
		execute,
		executeCallbacks,
		ignore,
		nextId,
		updateState,
		onFailure,
		onSuccess,
	);

	// Kick off an initial execution, if desired.
	if (executeImmediately) {
		(execute.current as any)(...(initialArguments ? initialArguments : []));
	} else {
		updateState.current({ type: "reset" }); // Poke the state to force a render the first time.
	}

	// The function returned from useEffect is executed on unmount or when the memo given
	// to useMemo changes. It is intended to tear down the previous effect. In our case, this
	// will prevent in-flight asyncs from trying to update the state of an unmounted component
	return () => handleTrackerUnmount(execute, ignore, updateState);
}
